import { Box, Text, VStack, Image, Button, Link } from "@chakra-ui/react";

import { LP_NETWORKS } from "../../constants/firebase";

import { trackEvent } from "../../utils/firebase";

import DiscontinuedBadge from "../DiscontinuedBadge";
import { getDiscontinued } from "../../constants/discontinued";

interface Props {
  label: string;
  icon: string;
  primaryCTAText: string;
  primaryCTAUrl: string;
  secondaryCTAText: string;
  secondaryCTAUrl: string;
  tvl: string | undefined;
  section?: string;
  tag?: string;
  isHeader?: boolean;
  dataKey?: string;
}

const NetworkCard = ({
  label,
  icon,
  primaryCTAText,
  primaryCTAUrl,
  secondaryCTAText,
  secondaryCTAUrl,
  tvl,
  section,
  tag,
  isHeader = false,
  dataKey,
}: Props) => {
  const discontinued = getDiscontinued(dataKey);
  const primaryText = discontinued?.primaryCTAText ?? primaryCTAText;
  const primaryUrl = discontinued?.primaryCTAUrl ?? primaryCTAUrl;
  const secondaryText = discontinued?.learnMoreText ?? secondaryCTAText;
  const secondaryUrl = discontinued?.learnMoreUrl ?? secondaryCTAUrl;

  const logEvent = (ctaText: string) => {
    trackEvent(LP_NETWORKS, {
      cta: section || "primary",
      name: label,
      cta_text: ctaText,
    });
  };

  return (
    <Box
      outline="none"
      bg="#E6F8EF"
      borderRadius={{ base: "10px", lg: "16px" }}
      pt={6}
      px={2}
      pb="0"
      minW={{ base: 104, lg: isHeader ? 176 : 205 }}
      maxH={{ base: 136, lg: 222 }}
      pos="relative"
      filter={discontinued ? "grayscale(1)" : undefined}
      opacity={discontinued ? 0.85 : 1}
      sx={{
        "&:hover": {
          bg: "radial-gradient(122.38% 69.59% at 50.3% 100%, #07C166 0%, #18C972 0.01%, #07A658 100%)",
          boxShadow: "0px 0px 32px rgba(20, 37, 40, 0.4)",
          opacity: 1,
        },
        "&:hover p.titleNetwork": {
          color: "white",
        },
        "&:hover p.subTitleNetwork": {
          color: "#FCD7D2",
        },
        "&:hover .cta": {
          visibility: "visible",
          opacity: 1,
        },
        "&:hover img": {
          transform: "translateY(-60px) scale(0.5714)",
          filter: "blur(8px)",
          opacity: 0.3,
        },
      }}
      transition="all 0.3s ease-in-out"
      overflow="hidden"
    >
      {discontinued && <DiscontinuedBadge label={discontinued.badge} />}
      <VStack
        gap={{ base: "32px", lg: "40px" }}
        textAlign="center"
        overflow="hidden"
      >
        <Box>
          <Text
            className="titleNetwork"
            fontSize={{ base: 14, lg: 24 }}
            fontWeight={600}
            color="textPrimary"
          >
            {label}
          </Text>
          <Text
            className="subTitleNetwork"
            fontSize={{ base: 10, lg: 16 }}
            fontWeight={600}
            color="textSecondary"
          >
            {tag ? (
              tag
            ) : tvl ? (
              `TVL : ${tvl}`
            ) : (
              <Box h={6} />
            )}
          </Text>
        </Box>
        <VStack
          className="cta"
          visibility="hidden"
          opacity={0}
          transition="all 0.6s ease"
          zIndex={1}
          pos="absolute"
          top="60%"
          transform="translateY(-25%)"
          transitionDelay="0.1s"
          transitionProperty="visibility, opacity"
        >
          {primaryText && (
            <Button
              as="a"
              href={primaryUrl}
              target="_blank"
              rel="canonical"
              bg="white !important"
              color="black !important"
              textStyle="p14"
              fontWeight={600}
              _hover={{ bg: "white" }}
              onClick={() => {
                logEvent(primaryText);
              }}
            >
              {primaryText}
            </Button>
          )}
          {secondaryText && (
            <Link
              variant="secondary"
              href={secondaryUrl}
              target="_blank"
              rel="canonical"
              color="white !important"
              borderColor="white !important"
              onClick={() => {
                logEvent(secondaryText);
              }}
            >
              {secondaryText}
            </Link>
          )}
        </VStack>
        <Image
          src={icon}
          boxSize={{ base: "86px", lg: "140px" }}
          alt={`${label}-icon`}
          transition="all 0.3s ease"
        />
      </VStack>
    </Box>
  );
};

export default NetworkCard;
